import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ChevronDownIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import { useContext } from "react";
import {
  BiHistory,
  BiHome,
  BiLogOut,
  BiMessageRoundedDetail,
  BiTime,
  BiUser,
} from "react-icons/bi";
import { FaUsers } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../context/Provider";
import { logout } from "../../context/actions/auth";
import { logoutApi } from "../../api/auth";
import RequestFormDrawer from "./sections/requestForms";
import { useQuery } from "react-query";
import { getRequestsApi, getSchedulesApi } from "../../api/appointments";
import { PERMISSIONS } from "../../auth/permissions";
import { getChatUnreadCountApi } from "../../api/chat";

const liveCountQueryOptions = {
  staleTime: 4000,
  refetchInterval: 5000,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  retry: 1,
};

const Navbar = ({ activePage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, authDispatch, can } = useContext(GlobalContext);
  const loggedIn = authState.isAuthenticated;
  const user = authState?.user;
  const canUseDashboard = can(PERMISSIONS.PATIENTS_CREATE);
  const requestFormDrawer = useDisclosure();
  const appointmentCountQuery = useQuery(
    ["navbarAppointmentCount", user?._id],
    getSchedulesApi,
    {
      ...liveCountQueryOptions,
      enabled: Boolean(
        loggedIn && user?._id && can(PERMISSIONS.APPOINTMENTS_VIEW),
      ),
    },
  );
  const requestCountQuery = useQuery(
    ["navbarRequestFormCount", user?._id],
    getRequestsApi,
    {
      ...liveCountQueryOptions,
      enabled: Boolean(
        loggedIn && user?._id && can(PERMISSIONS.REQUEST_FORMS_VIEW),
      ),
    },
  );
  const chatUnreadQuery = useQuery(
    ["chatUnreadCount", user?._id],
    getChatUnreadCountApi,
    {
      ...liveCountQueryOptions,
      refetchInterval: 15000,
      enabled: Boolean(loggedIn && user?._id && can(PERMISSIONS.CHAT_USE)),
    },
  );
  const appointmentCount = appointmentCountQuery.data?.data?.no || 0;
  const requestCount = requestCountQuery.data?.data?.no || 0;
  const chatUnreadCount = chatUnreadQuery.data?.data?.count || 0;
  const unitAdminLabel = user?.role === "unit_admin"
    ? `${String(user.unit || "Unit").replace(/\s+unit$/i, "")} Admin`
    : "Admin";

  const shouldViewAllPatients =
    authState?.user?.unit === "Medical record unit" ||
    authState?.user?.role === "super_admin" ||
    authState?.user?.role === "admin";

  const routes = [
    ...(canUseDashboard
      ? [{ icon: BiHome, title: "Dashboard", path: "/dashboard" }]
      : []),
    ...(can(PERMISSIONS.PATIENTS_VIEW) && shouldViewAllPatients
      ? [{ icon: FaUsers, title: "Patients", path: "/patients" }]
      : []),
    ...(can(PERMISSIONS.APPOINTMENTS_VIEW)
      ? [
          {
            icon: BiTime,
            title: "Attendances",
            path: "/appointments",
            count: appointmentCount,
            countLabel: "current appointments",
          },
        ]
      : []),
    ...(can(PERMISSIONS.CHAT_USE)
      ? [
          {
            icon: BiMessageRoundedDetail,
            title: "Chat",
            path: "/chat",
            count: chatUnreadCount,
            countLabel: "unread messages",
          },
        ]
      : []),
    ...(can(PERMISSIONS.WORKERS_MANAGE)
      ? [{ icon: BiUser, title: unitAdminLabel, path: "/dashboard/admin", activeAliases: ["Admin"] }]
      : []),
  ];

  const goHome = () => {
    if (!loggedIn) return navigate("/");
    navigate(
      canUseDashboard
        ? "/dashboard"
        : can(PERMISSIONS.APPOINTMENTS_VIEW)
          ? "/appointments"
          : "/profile",
    );
  };

  const handleLogout = async () => {
    try {
      await logoutApi();
    } finally {
      logout()(authDispatch);
      navigate("/auth/login");
    }
  };

  const NavItem = ({ route, mobile = false }) => {
    const selected =
      location.pathname === route.path || activePage === route.title || route.activeAliases?.includes(activePage);
    return (
      <Button
        variant={selected ? "solid" : "ghost"}
        colorScheme={selected ? "brand" : "gray"}
        color={selected ? "white" : "gray.600"}
        leftIcon={<Icon as={route.icon} boxSize="18px" />}
        justifyContent={mobile ? "flex-start" : "center"}
        w={mobile ? "full" : "auto"}
        onClick={() => navigate(route.path)}
      >
        <HStack spacing="2">
          <Text>{route.title}</Text>
          {route.count > 0 && (
            <Badge
              colorScheme={selected ? "whiteAlpha" : "red"}
              bg={selected ? "whiteAlpha.300" : undefined}
              color={selected ? "white" : undefined}
              borderRadius="full"
              minW="22px"
              px="1.5"
              textAlign="center"
              aria-label={`${route.count} ${route.countLabel || "items"}`}
            >
              {route.count > 99 ? "99+" : route.count}
            </Badge>
          )}
        </HStack>
      </Button>
    );
  };

  return (
    <Box as="header" bg="white" borderBottomWidth="1px" borderColor="gray.100">
      <Flex
        minH={{ base: "68px", md: "76px" }}
        maxW="1440px"
        mx="auto"
        px={{ base: "16px", md: "28px", xl: "48px" }}
        align="center"
        justify="space-between"
        gap="6"
      >
        <HStack spacing="3" cursor="pointer" onClick={goHome} flexShrink={0}>
          <Flex
            w="42px"
            h="42px"
            borderRadius="12px"
            bg="brand.600"
            color="white"
            align="center"
            justify="center"
            fontWeight="800"
            fontSize="lg"
          >
            O+
          </Flex>
          <VStack spacing="0" align="flex-start">
            <Text
              fontSize="xs"
              fontWeight="700"
              color="health.500"
              letterSpacing=".12em"
            >
              OAU
            </Text>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="800"
              color="gray.800"
            >
              Health Centre
            </Text>
          </VStack>
        </HStack>

        {loggedIn && (
          <HStack spacing="1" display={{ base: "none", lg: "flex" }}>
            {routes.map((route) => (
              <NavItem key={route.path} route={route} />
            ))}
          </HStack>
        )}

        {loggedIn ? (
          <HStack spacing="2">
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={<ChevronDownIcon />}
                px={{ base: "2", md: "3" }}
              >
                <HStack>
                  <Avatar
                    size="sm"
                    name={`${user?.firstName || ""} ${user?.lastName || ""}`}
                  />
                  <Box display={{ base: "none", md: "block" }} textAlign="left">
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      noOfLines={1}
                      maxW="180px"
                    >
                      {user?.firstName} {user?.lastName}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      noOfLines={1}
                      maxW="180px"
                    >
                      {user?.unit}
                    </Text>
                  </Box>
                  {requestCount > 0 && (
                    <Badge
                      colorScheme="red"
                      borderRadius="full"
                      minW="22px"
                      px="1.5"
                      textAlign="center"
                      aria-label={`${requestCount} request forms awaiting your action`}
                    >
                      {requestCount > 99 ? "99+" : requestCount}
                    </Badge>
                  )}
                </HStack>
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<BiUser />}
                  onClick={() => navigate("/profile")}
                >
                  Profile
                </MenuItem>
                <MenuItem
                  icon={<BiHistory />}
                  onClick={() => navigate("/my-activity")}
                >
                  My Activity
                </MenuItem>
                <MenuItem onClick={() => navigate("/dashboard/password")}>
                  Change password
                </MenuItem>
                {can(PERMISSIONS.REQUEST_FORMS_VIEW) && (
                  <MenuItem onClick={requestFormDrawer.onOpen}>
                    <Flex
                      align="center"
                      justify="space-between"
                      w="full"
                      gap="4"
                    >
                      <Text>Request Forms</Text>
                      {requestCount > 0 && (
                        <Badge
                          colorScheme="red"
                          borderRadius="full"
                          minW="22px"
                          px="1.5"
                          textAlign="center"
                        >
                          {requestCount > 99 ? "99+" : requestCount}
                        </Badge>
                      )}
                    </Flex>
                  </MenuItem>
                )}

                <MenuDivider />
                <MenuItem
                  icon={<BiLogOut />}
                  color="red.500"
                  onClick={handleLogout}
                >
                  Sign out
                </MenuItem>
              </MenuList>
            </Menu>
            <Menu>
              <MenuButton
                as={IconButton}
                display={{ base: "inline-flex", lg: "none" }}
                icon={<HamburgerIcon />}
                aria-label="Open navigation"
                variant="ghost"
              />
              <MenuList>
                {routes.map((route) => (
                  <MenuItem
                    key={route.path}
                    icon={<Icon as={route.icon} />}
                    onClick={() => navigate(route.path)}
                  >
                    <Flex
                      align="center"
                      justify="space-between"
                      w="full"
                      gap="4"
                    >
                      <Text>{route.title}</Text>
                      {route.count > 0 && (
                        <Badge
                          colorScheme="red"
                          borderRadius="full"
                          minW="22px"
                          px="1.5"
                          textAlign="center"
                        >
                          {route.count > 99 ? "99+" : route.count}
                        </Badge>
                      )}
                    </Flex>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>
        ) : (
          <HStack spacing="2">
            <Button
              display={{ base: "none", md: "inline-flex" }}
              variant="ghost"
              onClick={() => navigate("/patient/register-patient")}
            >
              Patient registration
            </Button>
            <Button variant="ghost" onClick={() => navigate("/auth/register")}>
              Health center staff sign up
            </Button>
            <Button onClick={() => navigate("/auth/login")}>Sign in</Button>
          </HStack>
        )}
      </Flex>

      {loggedIn && activePage && (
        <Flex
          bg="brand.50"
          minH="72px"
          px={{ base: "16px", md: "28px", xl: "48px" }}
          align="center"
        >
          <Center maxW="1344px" w="full" mx="auto">
            <Text
              fontWeight="800"
              fontSize={{ base: "lg", md: "2xl" }}
              color="brand.800"
            >
              {activePage}
            </Text>
          </Center>
        </Flex>
      )}
      <RequestFormDrawer requestFormDrawer={requestFormDrawer} />
    </Box>
  );
};

export default Navbar;
